import { Injectable } from '@angular/core';

import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams, HttpEvent, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment as environment } from 'src/environments/environment.dev';
import { Router } from '@angular/router';
import { HttpStatus } from '../constants/constants';
import { CookieService } from 'ngx-cookie-service';
import { LoggerService } from './logger.service';


@Injectable({
  providedIn: 'root'
})

/**
 * Api Service is the ease of user connector class
 * This is to be used for any api call towards our ABCis Apis
 */
export class ApiService {

  private configs: ApiConfig;

  constructor(private http: HttpClient,
        private router: Router,
        private cookieService: CookieService,
        private logger: LoggerService ) {}

  clientCredentials: string = environment.clientCredentials;

  /**
     * Gets the Url Configurations depending on the environment configurations.
     */
  async GetUrlConfigurations() {
    let authConfigUrl = '';

    // Get api configuration url
    authConfigUrl = `${this.generateEnvironmentUrl()}properties/apiconfig`;

    // Send a get request and set the base configurations
    this.configs = (await this.getBasicRequest<ApiConfig>(authConfigUrl, {'headers': new HttpHeaders().set('x-abcis-application','document-manager')})).body;

    // Return Configurations or HttpError
    return this.configs;
  }

  generateEnvironmentUrl(): string {
    return environment.authUrl;
  }

  ///////// Basic Requests

  /**
     * Get a simple request.
     * @param url The get request url
     * @param options any options being parsed through the request
     */
  public getBasicRequest<T>(url:string, options?): Promise<HttpResponse<T>> {
    // Set the flag to return a httpresponse, not just the body
    if (options == undefined) {
      options = {};
    }

    options.observe = 'response';

    // Send a get request with a promised result
    return new Promise((resolve, reject) => {
      this.http.get<T>(url, options).subscribe((response:HttpResponse<T>) =>
      { // Respectivly resolve or reject the response
        resolve(response);
      }, (error) => {
        console.log(error);
        reject(error);
      });
    });
  }

  /**
     * Post a simple request.
     * @param url The post url
     * @param body The body being posted through
     * @param options any options being parsed through the post request
     */
  public postBasicRequest<T>(url: string, body?: any, options?): Promise<HttpResponse<T>> {
    // Set the flag to return a httpresponse, not just the body
    if (options == undefined) options = {};
    options.observe = 'response';

    // Send a post that will promise a result
    return new Promise((resolve, reject) => {
      this.http.post<T>(url, body, options).subscribe((response:any) =>
      { // Respectivly resolve or reject the response
        resolve(response);
      }, (error) => {
        console.log(error);
        reject(error);
      });
    });
  }

  /**
     * Put a simple request.
     * @param url The put url
     * @param body The body being posted through
     * @param options any options being parsed through the put request
     */
  public putBasicRequest<T>(url: string, body:any, options?): Promise<HttpResponse<T>> {
    // Set the flag to return a httpresponse, not just the body
    if (options == undefined) options = {};
    options.observe = 'response';

    // Send a put that will promise a result
    return new Promise((resolve, reject) => {
      this.http.put<T>(url, body, options).subscribe((response:any) =>
      { // Respectivly resolve or reject the response
        resolve(response);
      }, (error) => {
        console.log(error);
        reject(error);
      });
    });
  }

  /**
     * Delete a simple request.
     * @param url The delete url
     * @param options any options being parsed through the delete request
     */
  public deleteBasicRequest<T>(url: string, options?): Promise<HttpResponse<T>> {
    // Set the flag to return a httpresponse, not just the body
    if (options == undefined) options = {};
    options.observe = 'response';

    // Send a delete that will promise a result
    return new Promise((resolve, reject) => {
      this.http.delete<T>(url, options).subscribe((response:any) =>
      { // Respectivly resolve or reject the response
        resolve(response);
      }, (error) => {
        console.log(error);
        reject(error);
      });
    });
  }

  ///////// ABCis Requests

  /**
     * Get an ABCis api request.
     * @param baseApi The ABCis api being connected to
     * @param url The connecting url
     * @param options any options being parsed into the request
     */
  public async getABCisRequest<T>(baseApi: ABCisApi, url: string, httpOptions?: HttpOptions): Promise<HttpResponse<T>> {
    // Generate the base address from enum
    const baseAddress = this.TranslateABCisApi(baseApi);

    // Set the auth header
    httpOptions = this.SetAuthHeaders(httpOptions);
    // Send a basic request that promises a result
    return await new Promise((resolve, reject) => {
      this.getBasicRequest<T>(baseAddress + url, httpOptions)
      // If the result went well, resolve it
        .then((val) => { resolve(val); })

      // If an error happened handle it.
        .catch((error) => {
          this.HandleABCisError(error).then(() => {
            // If the error has been successfully handled resolve another request
            // With the new access token
            httpOptions = this.SetAuthHeaders(httpOptions);
            resolve(this.getBasicRequest<T>(baseAddress + url, httpOptions));
          }).catch(() => {
            // Reject if the error wasn't handled
            reject(error);
          });
        });
    });
  }

  /**
     * Posts a ABCis api request.
     * @param baseApi The ABCis base api being posted
     * @param url The connecting url
     * @param body The body being parsed through
     * @param options any options being posted
     */
  public async postABCisRequest<T>(baseApi: ABCisApi, url: string, body?:any, httpOptions?: HttpOptions): Promise<HttpResponse<T>> {
    // Generate the base address from enum
    const baseAddress = this.TranslateABCisApi(baseApi);
    return this.sharedPostRequest<T>(baseAddress + url, body, httpOptions);
  }

  /**
     * Put a ABCis api request.
     * @param baseApi The ABCis base api being putted
     * @param url The connecting url
     * @param body The body being parsed through
     * @param options any options being putted
     */
  public async putABCisRequest<T>(baseApi: ABCisApi, url: string, body:any, httpOptions?: HttpOptions): Promise<HttpResponse<T>> {
    // Generate the base address from enum
    const baseAddress = this.TranslateABCisApi(baseApi);

    // Set the auth header
    httpOptions = this.SetAuthHeaders(httpOptions);

    // Send the put request that promises a result
    return await new Promise((resolve, reject) => {
      this.putBasicRequest<T>(baseAddress + url, body, httpOptions)
        // If the put was successful, resolve it
        .then((val) => { resolve(val); })

        // If an error happened handle it.
        .catch((error) => {
          this.HandleABCisError(error).then(() => {
            // If the error has been successfully handled resolve another request
            // With the new access token
            httpOptions = this.SetAuthHeaders(httpOptions);
            this.putBasicRequest<T>(baseAddress + url, body, httpOptions)
              .then(resolve)
              .catch(reject);
          }).catch(() => {
            // Reject if the error wasn't handled
            reject(error);
          });
        });
    });
  }

  /**
     * Delete an ABCis api request.
     * @param baseApi The ABCis api being connected to
     * @param url The connecting url
     * @param options any options being parsed into the request
     */
  public async deleteABCisRequest<T>(baseApi: ABCisApi, url: string, httpOptions?: HttpOptions): Promise<HttpResponse<T>> {
    // Generate the base address from enum
    const baseAddress = this.TranslateABCisApi(baseApi);

    // Set the auth header
    httpOptions = this.SetAuthHeaders(httpOptions);

    // Send a delete request that promises a result
    return await new Promise((resolve, reject) => {
      this.deleteBasicRequest<T>(baseAddress + url, httpOptions)
      // If the result went well, resolve it
        .then((val) => { resolve(val); })

      // If an error happened handle it.
        .catch((error) => {
          this.HandleABCisError(error).then(() => {
            // If the error has been successfully handled resolve another request
            // With the new access token
            httpOptions = this.SetAuthHeaders(httpOptions);
            resolve(this.deleteBasicRequest<T>(baseAddress + url, httpOptions));
          }).catch(() => {
            // Reject if the error wasn't handled
            reject(error);
          });
        });
    });
  }

  /**
   * Shared POST method for ABCis/external APIs
   * @param url The connecting url
   * @param body The body being parsed through
   * @param httpOptions any options being posted
   */
  private async sharedPostRequest<T>(url: string, body?: any, httpOptions?: HttpOptions): Promise<HttpResponse<T>> {
    httpOptions = this.SetAuthHeaders(httpOptions);

    // Send the post request that promises a result
    return await new Promise((resolve, reject) => {
      this.postBasicRequest<T>(url, body, httpOptions)
        // If the post was successful, resolve it
        .then((val) => {
          resolve(val);
        })
        // If an error happened, handle it
        .catch((error) => {
          this.HandleABCisError(error)
            .then(() => {
              // Retry with refreshed auth header
              httpOptions = this.SetAuthHeaders(httpOptions);
              resolve(this.postBasicRequest<T>(url, body, httpOptions));
            })
            .catch(() => {
              // Reject if the error wasn’t handled
              reject(error);
            });
        });
    });
  }

  /// Observable Events

  /**
     * Get a simple observable request.
     * @param url The get request url
     * @param options any options being parsed through the request
     */
  public getBasicObservableRequest<T>(url:string, options?): Observable<HttpEvent<T>> {
    // Set the flag to return a httpresponse, not just the body
    if (options == undefined) options = {};
    options.observe = 'response';

    // Send a get request with a promised result
    return this.http.get<T>(url, options);
  }

  /**
     * Get an ABCis api request.
     * @param baseApi The ABCis api being connected to
     * @param url The connecting url
     * @param options any options being parsed into the request
     */
  public getABCisObservableRequest<T>(baseApi: ABCisApi, url: string, httpOptions?: HttpOptions): Observable<HttpEvent<T>> {
    // Generate the base address from enum
    const baseAddress = this.TranslateABCisApi(baseApi);

    // Set the auth header
    httpOptions = this.SetAuthHeaders(httpOptions);

    // Send a basic request that promises a result
    return this.getBasicObservableRequest<T>(baseAddress + url, httpOptions);
  }

  /**
   * Post an external request with auth headers.
   * @param url The full external URL
   * @param body The payload to send
   * @param options Optional request options
   */
  public postExternalRequest<T>(url: string, body?: any, httpOptions?: HttpOptions): Promise<HttpResponse<T>> {
    return this.sharedPostRequest<T>(url, body, httpOptions);
  }

  ///////// Auth Request
  /**
     * Posts a authentication request
     * @param baseApi The ABCis base api being posted
     * @param url The connecting url
     * @param body The body being parsed through
     */
  public async authRequest<T>(baseApi: ABCisApi, url: string, body: FormData) {
    // Generate the base address from enum
    const baseAddress = this.TranslateABCisApi(baseApi);

    // Use browser-native btoa() for Base64 encoding - faster, smaller bundle, no polyfills needed
    const base64Credentials = btoa(this.clientCredentials);
    let headers = new HttpHeaders();
    headers = headers.set('Authorization','Basic ' + base64Credentials);
    const options = new HttpOptions();
    options.headers = headers;

    // Post the basic client request
    return await this.postBasicRequest<T>(baseAddress + url, body, options);
  }

  /**
     * Sets the authorization header inside the http options.
     * @param options the options being altered
     */
  SetAuthHeaders(options: HttpOptions = new HttpOptions()): HttpOptions {
    if (this.cookieService.check('USER_TOKEN')) {
      let headers = new HttpHeaders();
      headers = headers.set('Authorization','Bearer ' +  this.cookieService.get('USER_TOKEN'));
      //headers.append("X-Requested-With", "XMLHttpRequest");
      options.withCredentials = true;
      options.headers = headers;
    }
    return options;
  }

  /**
     * Translates the baseApi enum to its url part
     * @param api The base api being returned
     */
  private TranslateABCisApi(api: ABCisApi): string {
    switch (api) {
    case ABCisApi.Auth:
      return this.configs.authBaseAddress;
    case ABCisApi.Gateway:
      return this.configs.gatewayBaseAddress;
    case ABCisApi.Files:
      return this.configs.filesBaseAddress;
    case ABCisApi.Jira:
      return this.configs.jiraBaseAddress;
    case ABCisApi.LegacyFiles:
      return this.configs.legacyFilesBaseAddress;
    case ABCisApi.Utils:
      return this.configs.utilsBaseAddress;
    case ABCisApi.Orders:
      return this.configs.ordersBaseAddress;
    case ABCisApi.LsRetail:
      return this.configs.lsRetailBaseAddress;
    case ABCisApi.Ocr:
      return this.configs.ocrApiBaseAddress;
    case ABCisApi.Support:
      return this.configs.supportApiBaseAddress;
      // return 'http://localhost:8083/';
            // case ABCisApi.Mock:
            //     return this.configs.mockApiBaseAddress;
    }
  }

  /**
     * Returns the current configuration.
     */
  GetConfig(): ApiConfig{
    return this.configs;
  }

  /**
     * Handles an error, If the error is a 401: Refresh the token
     * @param error the error that is being handler
     */
  HandleABCisError(error: HttpErrorResponse): Promise<any> {
    return new Promise((resolve, reject) => {
      // Check if the error is an UNAUTHORIZED ERROR
      if (error.status == HttpStatus.UNAUTHORIZED) {
        // If the user doesn't have a refresh token or a user token. Log them out
        if (!this.cookieService.check('REFRESH_TOKEN') || !this.cookieService.check('USER_TOKEN')) {
          this.LogOutUser();
          reject();
          return;
        }

        // Set the refresh token headers
        const form = new FormData();
        form.append('grant_type', 'refresh_token');
        form.append('refresh_token', this.cookieService.get('REFRESH_TOKEN'));

        // Send a refresh token post request
        this.authRequest<any>(ABCisApi.Auth, 'oauth/token', form)
          .then((httpResponse) => {
            // If the response is a valid token response. Set the cookies.
            const response = httpResponse.body;
            if (response.access_token !== undefined && response.access_token !== null
              && response.refresh_token !== undefined && response.refresh_token !== null) {
              this.cookieService.set('USER_TOKEN', response.access_token, 1, '/', environment.domain, environment.isSecure, 'Strict' );
              this.cookieService.set('REFRESH_TOKEN', response.refresh_token, 1, '/', environment.domain, environment.isSecure, 'Strict' );
              this.logger.debug('Token Refreshed!');
              resolve(true); // This was missing!
            } else {
              this.LogOutUser();
              reject();
            }
          }).catch((val) => {
            console.log(val);
            this.LogOutUser();
            reject();
          });
      } else {
        reject();
      }
    });
  }

  /**
     * Logs out the user
     */
  LogOutUser() {
    this.logger.info('Deleting tokens and logout');
    this.cookieService.delete('USER_TOKEN', '/', environment.domain);
    this.cookieService.delete('REFRESH_TOKEN', '/', environment.domain);
    this.router.navigateByUrl('/');
  }
}

/**
 * The base api enum.
 */
export enum ABCisApi {
    Auth = 0,
    Gateway = 1,
    Files = 2,
    Jira = 3,
    LegacyFiles = 4,
    Utils = 5,
    Orders = 6,
    LsRetail = 7,
    Ocr = 8,
    Support = 9,
}

/**
 * The base addresses used for each ABCis Api
 */
class ApiConfig
{
  authBaseAddress: string;
  gatewayBaseAddress: string;
  filesBaseAddress: string;
  jiraBaseAddress: string;
  legacyFilesBaseAddress: string;
  utilsBaseAddress: string;
  ordersBaseAddress: string;
  lsRetailBaseAddress: string;
  ocrApiBaseAddress: string;
  supportApiBaseAddress: string;
  // mockApiBaseAddress:string;
}

/**
 * This class is a mimic of the Http Options? request variable.
 * Used for configuring authorization headers programmaticly.
 */
export class HttpOptions {
  headers?: HttpHeaders | {
        [header: string]: string | string[];
    };
  observe?: 'body';
  params?: HttpParams | {
        [param: string]: string | string[];
    };
  reportProgress?: boolean;
  responseType: any;
  withCredentials?: boolean;
}

export class UrlCriteria {
  name: string;
  value: any;
}
